import { useState, useRef, useEffect } from "react"
import {
  Plus,
  FileText,
  Image as ImageIcon,
  Video,
  HelpCircle,
  Download, ChevronRight,
  ChevronDown,
  Loader2, ArrowUp,
  ArrowDown,
  Trash2,
  BookOpen
} from "lucide-react"
import { Button } from "../ui/button"
// Temporarily disabled drag and drop due to React 19 compatibility issues
// import {
//   DndContext,
//   closestCenter,
//   KeyboardSensor,
//   PointerSensor,
//   useSensor,
//   useSensors,
//   DragOverlay
// } from "@dnd-kit/core"
// import {
//   arrayMove,
//   SortableContext,
//   sortableKeyboardCoordinates,
//   verticalListSortingStrategy,
//   useSortable
// } from "@dnd-kit/sortable"
// import { CSS } from "@dnd-kit/utilities"

// Sortable components temporarily disabled due to React 19 compatibility issues
// Will be re-enabled when @dnd-kit/core is compatible with React 19

export default function EditorSidebar({
  modules,
  selectedModuleId,
  selectedLessonId, // NEW: Lesson selection
  selectedBlockId,
  selectedQuizId,
  onModuleSelect,
  onLessonSelect, // NEW: Lesson selection handler
  onBlockSelect,
  onQuizSelect,
  onCreateModule,
  onCreateLesson, // NEW: Create lesson handler
  onCreateBlock,
  onCreateQuiz,
  onModuleReorder,
  onBlockReorder,
  onModuleMoveUp,
  onModuleMoveDown,
  onModuleDelete,
  onLessonDelete, // NEW: Delete lesson handler
  onBlockDelete,
  onQuizDelete,
  isCreatingModule = false,
  isReorderingBlocks = false,
  course = null,
  onCourseSelect = null,
  selectedCourseId = null
}) {
  const [expandedCourse, setExpandedCourse] = useState(true) // Le cours est expandé par défaut
  const [expandedModules, setExpandedModules] = useState(new Set())
  const [expandedLessons, setExpandedLessons] = useState(new Set()) // NEW: Expanded lessons
  const [openDropdownModuleId, setOpenDropdownModuleId] = useState(null)
  const [openDropdownLessonId, setOpenDropdownLessonId] = useState(null) // NEW: Dropdown for lessons
  const dropdownRefs = useRef({})
  const lessonDropdownRefs = useRef({}) // NEW: Refs for lesson dropdowns

  const toggleCourse = () => {
    setExpandedCourse(prev => !prev)
  }

  const toggleModule = (moduleId) => {
    setExpandedModules(prev => {
      const newSet = new Set(prev)
      if (newSet.has(moduleId)) {
        newSet.delete(moduleId)
      } else {
        newSet.add(moduleId)
      }
      return newSet
    })
  }

  // NEW: Toggle lesson expansion
  const toggleLesson = (lessonId) => {
    setExpandedLessons(prev => {
      const newSet = new Set(prev)
      if (newSet.has(lessonId)) {
        newSet.delete(lessonId)
      } else {
        newSet.add(lessonId)
      }
      return newSet
    })
  }

  // Drag and drop handlers temporarily disabled due to React 19 compatibility issues
  // TODO: Re-enable when @dnd-kit/core is compatible with React 19

  const handleCreateModuleClick = () => {
    onCreateModule()
  }

  const handleCreateBlockClick = (type, lessonId) => {
    if (!lessonId && !selectedLessonId) {
      alert('Veuillez sélectionner un chapitre d\'abord')
      return
    }
    onCreateBlock(type, lessonId || selectedLessonId)
  }

  const handleCreateQuizClick = () => {
    if (!selectedModuleId) {
      alert('Veuillez sélectionner un module d\'abord')
      return
    }
    onCreateQuiz(selectedModuleId)
  }

  const handleAddBlockClick = (e, lessonId, type) => {
    e.stopPropagation()
    setOpenDropdownLessonId(null)
    onCreateBlock(type, lessonId)
  }

  // NEW: Handle add lesson click
  const handleAddLessonClick = (e, moduleId) => {
    e.stopPropagation()
    setOpenDropdownModuleId(null)
    if (onCreateLesson) {
      onCreateLesson(moduleId)
    }
  }

  const handleAddQuizClick = (e, moduleId) => {
    e.stopPropagation()
    setOpenDropdownModuleId(null)
    onCreateQuiz(moduleId)
  }

  const toggleDropdown = (e, moduleId) => {
    e.stopPropagation()
    setOpenDropdownModuleId(openDropdownModuleId === moduleId ? null : moduleId)
  }

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openDropdownModuleId && dropdownRefs.current[openDropdownModuleId]) {
        if (!dropdownRefs.current[openDropdownModuleId].contains(event.target)) {
          setOpenDropdownModuleId(null)
        }
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [openDropdownModuleId])

  const moduleItems = modules.map(m => m.id)
  // Récupérer tous les blocs de tous les modules pour les items de drag and drop
  const allBlocks = modules.flatMap(m => m.blocks || m.contentBlocks || [])
  const blockItems = allBlocks.map(b => b.id)

  // Trier les modules par ordre
  const sortedModules = [...modules].sort((a, b) => (a.order || 0) - (b.order || 0))

  return (
    <aside className="w-80 bg-white border-r border-gray-200 flex flex-col overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-center justify-end">
          <Button
            size="sm"
            onClick={handleCreateModuleClick}
            disabled={isCreatingModule}
            className="h-8 bg-[#141835] hover:bg-[#1a1f4a] text-white"
          >
            {isCreatingModule ? (
              <>
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                Création...
              </>
            ) : (
              <>
                <Plus className="w-4 h-4 mr-1" />
                Module
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4">
        {/* Temporarily disable DndContext due to React 19 compatibility issues */}
        {/* TODO: Re-enable drag and drop when @dnd-kit/core is compatible with React 19 */}
        {course && (
          <div className="mb-4">
            {/* Course Parent */}
            <div
              className={`flex items-center gap-2 p-3 rounded-lg cursor-pointer transition-colors ${
                !selectedModuleId && !selectedBlockId && !selectedQuizId && selectedCourseId === course.id
                  ? 'bg-[#203a8a] text-white'
                  : 'bg-white hover:bg-gray-50 border border-gray-200'
              }`}
              onClick={() => {
                if (onCourseSelect) {
                  onCourseSelect(course.id)
                }
              }}
            >
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  toggleCourse()
                }}
                className="p-1 hover:bg-gray-200 rounded"
              >
                {expandedCourse ? (
                  <ChevronDown className="w-4 h-4" />
                ) : (
                  <ChevronRight className="w-4 h-4" />
                )}
              </button>
              <div className="flex items-center gap-2 flex-1 min-w-0">
                {course.thumbnail && (
                  <div className="w-8 h-8 rounded-lg overflow-hidden border border-gray-200 shadow-sm flex-shrink-0">
                    <img
                      src={course.thumbnail}
                      alt={course.title || 'Cours'}
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}
                <BookOpen className="w-4 h-4 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{course.title || 'Cours'}</div>
                  <div className={`text-xs truncate ${!selectedModuleId && !selectedBlockId && !selectedQuizId && selectedCourseId === course.id ? 'text-gray-200' : 'text-gray-500'}`}>
                    {sortedModules.length || 0} module{sortedModules.length !== 1 ? 's' : ''}
                  </div>
                </div>
              </div>
            </div>
            
            {/* Modules as children of course */}
            {expandedCourse && (
              <div className="ml-4 mt-2">
                {sortedModules.length === 0 ? (
                  <div className="text-center py-4 text-gray-500">
                    <p className="mb-2 text-sm">Aucun module</p>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={handleCreateModuleClick}
                      disabled={isCreatingModule}
                      className="h-7 text-xs"
                    >
                      {isCreatingModule ? (
                        <>
                          <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                          Création...
                        </>
                      ) : (
                        'Créer un module'
                      )}
                    </Button>
                  </div>
                ) : (
                  sortedModules.map((module, index) => {
                    const isExpanded = expandedModules.has(module.id)
                    const isSelected = module.id === selectedModuleId
                    // NEW: Récupérer les leçons du module (les blocs sont maintenant dans les leçons)
                    const moduleLessons = (module.lessons || [])
                      .sort((a, b) => (a.order || 0) - (b.order || 0))

                    return (
                      <div key={module.id} className="mb-2">
                        <div
                          className={`flex items-center gap-2 p-3 rounded-lg cursor-pointer transition-colors relative ${
                            isSelected
                              ? 'bg-[#203a8a] text-white'
                              : 'bg-white hover:bg-gray-50 border border-gray-200'
                          }`}
                          onClick={() => onModuleSelect(module.id)}
                        >
                          <button
                            onClick={(e) => {
                              e.stopPropagation()
                              toggleModule(module.id)
                            }}
                            className="p-1 hover:bg-gray-200 rounded"
                          >
                            {isExpanded ? (
                              <ChevronDown className="w-4 h-4" />
                            ) : (
                              <ChevronRight className="w-4 h-4" />
                            )}
                          </button>
                          <div className="flex-1 min-w-0">
                            <div className="font-medium truncate">{module.title}</div>
                            <div className={`text-xs truncate ${isSelected ? 'text-gray-200' : 'text-gray-500'}`}>
                              {moduleLessons.length || 0} chapitre{moduleLessons.length !== 1 ? 's' : ''}
                              {module.quiz && (module.quiz.quiz || module.quiz) && (
                                <span className="ml-1">• Quiz</span>
                              )}
                            </div>
                          </div>
                          {/* Boutons monter/descendre */}
                          {onModuleMoveUp && onModuleMoveDown && (
                            <div className="flex items-center gap-1">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation()
                                  if (index > 0) {
                                    onModuleMoveUp(module.id, index)
                                  }
                                }}
                                disabled={index === 0}
                                className={`p-1.5 rounded transition-colors ${
                                  index === 0
                                    ? 'opacity-30 cursor-not-allowed'
                                    : isSelected
                                      ? 'hover:bg-white/20 text-white'
                                      : 'hover:bg-gray-200 text-gray-600'
                                }`}
                                title="Monter"
                              >
                                <ArrowUp className="w-4 h-4" />
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation()
                                  if (index < sortedModules.length - 1) {
                                    onModuleMoveDown(module.id, index)
                                  }
                                }}
                                disabled={index >= sortedModules.length - 1}
                                className={`p-1.5 rounded transition-colors ${
                                  index >= sortedModules.length - 1
                                    ? 'opacity-30 cursor-not-allowed'
                                    : isSelected
                                      ? 'hover:bg-white/20 text-white'
                                      : 'hover:bg-gray-200 text-gray-600'
                                }`}
                                title="Descendre"
                              >
                                <ArrowDown className="w-4 h-4" />
                              </button>
                            </div>
                          )}
                          {/* Bouton supprimer module */}
                          {onModuleDelete && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                if (window.confirm(`Êtes-vous sûr de vouloir supprimer le module "${module.title}" ? Cette action est irréversible.`)) {
                                  onModuleDelete(module.id)
                                }
                              }}
                              className={`p-1.5 rounded transition-colors ${
                                isSelected
                                  ? 'hover:bg-white/20 text-white'
                                  : 'hover:bg-gray-200 text-red-600'
                              }`}
                              title="Supprimer le module"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                          <div className="relative" ref={el => dropdownRefs.current[module.id] = el}>
                            <button
                              onClick={(e) => toggleDropdown(e, module.id)}
                              className={`p-1 rounded hover:bg-opacity-20 ${
                                isSelected ? 'hover:bg-white' : 'hover:bg-gray-200'
                              }`}
                            >
                              <Plus className={`w-4 h-4 ${isSelected ? 'text-white' : 'text-gray-600'}`} />
                            </button>
                            {openDropdownModuleId === module.id && (
                              <div className="absolute right-0 top-full mt-1 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
                                <div className="py-1">
                                  <button
                                    onClick={(e) => handleAddLessonClick(e, module.id)}
                                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                                  >
                                    <BookOpen className="w-4 h-4 text-gray-600" />
                                    chapitre
                                  </button>
                                  <div className="border-t border-gray-200 my-1"></div>
                                  <button
                                    onClick={(e) => handleAddQuizClick(e, module.id)}
                                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                                  >
                                    <HelpCircle className="w-4 h-4 text-gray-600" />
                                    Quiz
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                        
                        {isExpanded && (
                          <div className="ml-4">
                            {/* NEW: Afficher les leçons */}
                            {moduleLessons.length > 0 ? (
                              moduleLessons.map((lesson) => {
                                const isLessonExpanded = expandedLessons.has(lesson.id)
                                const isLessonSelected = lesson.id === selectedLessonId
                                const lessonBlocks = (lesson.blocks || [])
                                  .sort((a, b) => (a.order || 0) - (b.order || 0))

                                return (
                                  <div key={lesson.id} className="mb-2">
                                    <div
                                      className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer transition-colors relative ${
                                        isLessonSelected
                                          ? 'bg-blue-100 border border-blue-300'
                                          : 'bg-gray-50 hover:bg-gray-100 border border-gray-200'
                                      }`}
                                      onClick={() => onLessonSelect && onLessonSelect(lesson.id)}
                                    >
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation()
                                          toggleLesson(lesson.id)
                                        }}
                                        className="p-1 hover:bg-gray-200 rounded"
                                      >
                                        {isLessonExpanded ? (
                                          <ChevronDown className="w-3 h-3" />
                                        ) : (
                                          <ChevronRight className="w-3 h-3" />
                                        )}
                                      </button>
                                      <div className="flex-1 min-w-0">
                                        <div className={`text-sm font-medium truncate ${isLessonSelected ? 'text-blue-900' : 'text-gray-700'}`}>
                                          {lesson.title}
                                        </div>
                                        <div className={`text-xs truncate ${isLessonSelected ? 'text-blue-700' : 'text-gray-500'}`}>
                                          {lessonBlocks.length || 0} bloc{lessonBlocks.length !== 1 ? 's' : ''}
                                        </div>
                                      </div>
                                      {onLessonDelete && (
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation()
                                            if (window.confirm(`Êtes-vous sûr de vouloir supprimer le chapitre "${lesson.title}" ?`)) {
                                              onLessonDelete(lesson.id)
                                            }
                                          }}
                                          className="p-1 rounded hover:bg-red-100 text-red-600"
                                          title="Supprimer le chapitre"
                                        >
                                          <Trash2 className="w-3 h-3" />
                                        </button>
                                      )}
                                      <div className="relative" ref={el => lessonDropdownRefs.current[lesson.id] = el}>
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation()
                                            setOpenDropdownLessonId(openDropdownLessonId === lesson.id ? null : lesson.id)
                                          }}
                                          className="p-1 rounded hover:bg-gray-200"
                                        >
                                          <Plus className="w-3 h-3 text-gray-600" />
                                        </button>
                                        {openDropdownLessonId === lesson.id && (
                                          <div className="absolute right-0 top-full mt-1 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
                                            <div className="py-1">
                                              <button
                                                onClick={(e) => handleAddBlockClick(e, lesson.id, 'TEXT')}
                                                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                                              >
                                                <FileText className="w-4 h-4 text-gray-600" />
                                                Bloc Texte
                                              </button>
                                              <button
                                                onClick={(e) => handleAddBlockClick(e, lesson.id, 'IMAGE')}
                                                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                                              >
                                                <ImageIcon className="w-4 h-4 text-gray-600" />
                                                Bloc Image
                                              </button>
                                              <button
                                                onClick={(e) => handleAddBlockClick(e, lesson.id, 'VIDEO')}
                                                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                                              >
                                                <Video className="w-4 h-4 text-gray-600" />
                                                Bloc Vidéo
                                              </button>
                                              <button
                                                onClick={(e) => handleAddBlockClick(e, lesson.id, 'DOWNLOAD')}
                                                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                                              >
                                                <Download className="w-4 h-4 text-gray-600" />
                                                Document téléchargeable
                                              </button>
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                    
                                    {/* Afficher les blocs de la leçon */}
                                    {isLessonExpanded && (
                                      <div className="ml-4 mt-1">
                                        {lessonBlocks.length > 0 ? (
                                          lessonBlocks.map((block, blockIndex) => {
                                            const getBlockIcon = (type) => {
                                              switch (type) {
                                                case 'TEXT':
                                                  return <FileText className="w-3 h-3" />
                                                case 'IMAGE':
                                                  return <ImageIcon className="w-3 h-3" />
                                                case 'VIDEO':
                                                  return <Video className="w-3 h-3" />
                                                case 'ASSIGNMENT':
                                                  return <HelpCircle className="w-3 h-3" />
                                                case 'DOWNLOAD':
                                                  return <Download className="w-3 h-3" />
                                                default:
                                                  return <FileText className="w-3 h-3" />
                                              }
                                            }
                                            const isBlockSelected = block.id === selectedBlockId
                                            const canMoveUp = blockIndex > 0
                                            const canMoveDown = blockIndex < lessonBlocks.length - 1
                                            
                                            return (
                                              <div
                                                key={block.id}
                                                className={`flex items-center gap-2 p-2 rounded cursor-pointer transition-colors ${
                                                  isBlockSelected
                                                    ? 'bg-[#203a8a] text-white'
                                                    : 'bg-white hover:bg-gray-50 border border-gray-200'
                                                }`}
                                                onClick={() => onBlockSelect && onBlockSelect(block.id)}
                                              >
                                                {getBlockIcon(block.type)}
                                                <div className="flex-1 min-w-0">
                                                  <div className={`text-xs font-medium truncate ${isBlockSelected ? 'text-white' : 'text-gray-700'}`}>
                                                    {block.title}
                                                  </div>
                                                </div>
                                                <div className="flex items-center gap-1">
                                                  {/* Boutons de réordonnancement */}
                                                  {onBlockReorder && (
                                                    <>
                                                      {isReorderingBlocks ? (
                                                        <Loader2 className="w-3 h-3 animate-spin text-gray-400" />
                                                      ) : (
                                                        <>
                                                          <button
                                                            onClick={(e) => {
                                                              e.stopPropagation()
                                                              if (canMoveUp && !isReorderingBlocks) {
                                                                const newOrder = [...lessonBlocks]
                                                                // Échanger avec le bloc précédent
                                                                const temp = newOrder[blockIndex - 1]
                                                                newOrder[blockIndex - 1] = newOrder[blockIndex]
                                                                newOrder[blockIndex] = temp
                                                                const blockIds = newOrder.map(b => b.id)
                                                                onBlockReorder(lesson.id, blockIds)
                                                              }
                                                            }}
                                                            disabled={!canMoveUp || isReorderingBlocks}
                                                            className={`p-1 rounded transition-colors ${
                                                              isBlockSelected
                                                                ? canMoveUp && !isReorderingBlocks
                                                                  ? 'hover:bg-white/20 text-white'
                                                                  : 'opacity-30 text-white cursor-not-allowed'
                                                                : canMoveUp && !isReorderingBlocks
                                                                ? 'hover:bg-gray-200 text-gray-600'
                                                                : 'opacity-30 text-gray-400 cursor-not-allowed'
                                                            }`}
                                                            title="Déplacer vers le haut"
                                                          >
                                                            <ArrowUp className="w-3 h-3" />
                                                          </button>
                                                          <button
                                                            onClick={(e) => {
                                                              e.stopPropagation()
                                                              if (canMoveDown && !isReorderingBlocks) {
                                                                const newOrder = [...lessonBlocks]
                                                                // Échanger avec le bloc suivant
                                                                const temp = newOrder[blockIndex]
                                                                newOrder[blockIndex] = newOrder[blockIndex + 1]
                                                                newOrder[blockIndex + 1] = temp
                                                                const blockIds = newOrder.map(b => b.id)
                                                                onBlockReorder(lesson.id, blockIds)
                                                              }
                                                            }}
                                                            disabled={!canMoveDown || isReorderingBlocks}
                                                            className={`p-1 rounded transition-colors ${
                                                              isBlockSelected
                                                                ? canMoveDown && !isReorderingBlocks
                                                                  ? 'hover:bg-white/20 text-white'
                                                                  : 'opacity-30 text-white cursor-not-allowed'
                                                                : canMoveDown && !isReorderingBlocks
                                                                ? 'hover:bg-gray-200 text-gray-600'
                                                                : 'opacity-30 text-gray-400 cursor-not-allowed'
                                                            }`}
                                                            title="Déplacer vers le bas"
                                                          >
                                                            <ArrowDown className="w-3 h-3" />
                                                          </button>
                                                        </>
                                                      )}
                                                    </>
                                                  )}
                                                {onBlockDelete && (
                                                  <button
                                                    onClick={(e) => {
                                                      e.stopPropagation()
                                                      if (window.confirm(`Êtes-vous sûr de vouloir supprimer le bloc "${block.title}" ?`)) {
                                                        onBlockDelete(block.id)
                                                      }
                                                    }}
                                                    className={`p-1 rounded transition-colors ${
                                                      isBlockSelected
                                                        ? 'hover:bg-white/20 text-white'
                                                        : 'hover:bg-gray-200 text-red-600'
                                                    }`}
                                                    title="Supprimer le bloc"
                                                  >
                                                    <Trash2 className="w-3 h-3" />
                                                  </button>
                                                )}
                                                </div>
                                              </div>
                                            )
                                          })
                                        ) : (
                                          <div className="text-xs text-gray-400 p-2">Aucun bloc</div>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                )
                              })
                            ) : (
                              <div className="text-xs text-gray-400 p-2">Aucune chapitre</div>
                            )}
                            {/* Afficher le quiz du module */}
                            {module.quiz && (module.quiz.quiz || module.quiz) && (
                              <div
                                className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer transition-colors ${
                                  (module.quiz.quiz || module.quiz).id === selectedQuizId
                                    ? 'bg-[#203a8a] text-white'
                                    : 'bg-white hover:bg-gray-50 border border-gray-200'
                                }`}
                                onClick={() => onQuizSelect && onQuizSelect((module.quiz.quiz || module.quiz).id)}
                              >
                                <HelpCircle className="w-4 h-4" />
                                <div className="flex-1 min-w-0">
                                  <div className="text-sm font-medium truncate">
                                    {(module.quiz.quiz || module.quiz).title || 'Quiz'}
                                  </div>
                                </div>
                                {onQuizDelete && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      if (window.confirm('Êtes-vous sûr de vouloir supprimer ce quiz ?')) {
                                        onQuizDelete((module.quiz.quiz || module.quiz).id)
                                      }
                                    }}
                                    className="p-1 rounded hover:bg-red-100 text-red-600"
                                    title="Supprimer le quiz"
                                  >
                                    <Trash2 className="w-3 h-3" />
                                  </button>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )
                  })
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </aside>
  )
}

